import { ImageProps } from 'components/atoms/Image';
import { HealthGoalTypesType } from 'components/molecules/HealthGoalSelection';
import { ActionType as DashboardActionType } from 'store/dashboard';
import { ActionType as ProfileActionType, actionTypes as profileAT } from 'store/profile';
import { REQUEST_SAVE_DASHBOARD_MEASUREMENT_SUCCESS } from '../dashboard/dashboard.actionTypes';
import { ProgramContainsType } from '../programPreview';
import { ProgramOverviewActionType } from './programOverview.actionCreators';
import { LOAD_FAILURE, LOAD_SUCCESS, SET_LOADING_STATE } from './programOverview.actionTypes';

export type BlackListItemNames = 'NUTRITION_PHILOSOPHY' | 'ALLERGIES';
export type BlackListItemValues =
	| 'root.vegan.'
	| 'root.meat-fish.'
	| 'root.gluten.'
	| 'root.lactose.'
	| 'root.vegetarian.';

export type BlackListItem = {
	name: BlackListItemNames;
	value: BlackListItemValues;
};

export type ProgramOverviewItemType = {
	contains: ProgramContainsType;
	duration?: any;
	blacklist: BlackListItem[];
	externalId: string;
	goal: HealthGoalTypesType;
	inactive: boolean;
	intensity: number;
	isNewProgram: boolean;
	previewActive: boolean;
	previewImage: ImageProps;
	previewImageMale: ImageProps;
	previewImageFemale: ImageProps;
	seoTitle: string;
	calories?: any;
	slug: string;
	title: string;
};

export type ProgramOverviewUserDataType = {
	pregnant: null | boolean;
	bmi: null | number;
	healthGoal: HealthGoalTypesType;
};

export type ProgramOverviewStateType = {
	error: null | Error;
	loading: boolean;
	loaded: boolean;
	externalIdList: string[];
	requiredUserData: ProgramOverviewUserDataType;
	programByExternalId: {
		[externalId: string]: ProgramOverviewItemType;
	};
	mapSlugToExternalId: {
		[slug: string]: string;
	};
	mapExternalIdToSlug: {
		[id: string]: string;
	};
};

export type RootStateType = {
	programOverview: ProgramOverviewStateType;
};

export const initialState: any = {
	error: null,
	loading: false,
	loaded: false,
	programByExternalId: {},
	lastSelectedTab: 'recommended',
	externalIdList: [],
	mapSlugToExternalId: {},
	mapExternalIdToSlug: {},
	requiredUserData: {
		pregnant: null,
		bmi: null,
	},
};

export default (
	state: ProgramOverviewStateType = initialState,
	action: ProgramOverviewActionType | ProfileActionType | DashboardActionType
) => {
	switch (action.type) {
		case SET_LOADING_STATE:
			return {
				...state,
				loading: !!action.isLoading,
			};

		case profileAT.REQUEST_USER_DATA_SUCCESS:
		case profileAT.REQUEST_SAVE_GENDER_SUCCESS:
		case profileAT.REQUEST_FINISH_ANAMNESIS_SUCCESS:
		case REQUEST_SAVE_DASHBOARD_MEASUREMENT_SUCCESS:
			const { data } = action as any;

			if (!data) {
				return state;
			}

			return {
				...state,
				requiredUserData: {
					bmi: data.bmi || null,
					pregnant: typeof data.pregnant === 'boolean' ? data.pregnant : false,
					healthGoal: data.healthGoal,
				},
			};
		case LOAD_SUCCESS:
			if (!action || !action.programOverviewInformation || !Array.isArray(action.programOverviewInformation)) {
				/*
				 * Prevent any errors in case if offline
				 * @see PULSWM-2663
				 * @author jbiasi
				 */
				return state;
			}

			const mapByExternalId: { [id: string]: ProgramOverviewItemType } = {};
			const mapSlugToExternalId: { [slug: string]: string } = {};
			const mapExternalIdToSlug: { [id: string]: string } = {};
			const externalIdList: string[] = [];

			action.programOverviewInformation.forEach((program: ProgramOverviewItemType) => {
				externalIdList.push(program.externalId);
				program.contains = program.contains || {};

				mapByExternalId[program.externalId] = {
					...program,
					duration: program.contains.days,
					calories: program.contains.calories || 0,
				};

				mapSlugToExternalId[program.slug] = program.externalId;
				mapExternalIdToSlug[program.externalId] = program.slug;
			});

			return {
				...state,
				mapSlugToExternalId,
				mapExternalIdToSlug,
				programByExternalId: mapByExternalId,
				externalIdList,
				loaded: true,
				loading: false,
			};
		case LOAD_FAILURE:
			return {
				...state,
				error: action.error,
				loaded: false,
				loading: false,
			};
		default:
			return state;
	}
};
